<template>
  <Modal
    title="Attach a Form"
    class-name="small-modal"
    submit-btn-text="Attach"
    :saving="saving"
    :disable-submit="!selectedId"
    @hide-modal="emit('hide-modal')"
    @submit="emit('submit', selectedId)"
  >
    <p v-if="loading" class="cf-hint" data-cy="cf-templates-loading">
      Loading forms...
    </p>

    <!-- Distinct from the empty state: telling an admin to publish a form they
         already published sends them on a false errand. -->
    <p v-else-if="loadFailed" class="cf-hint" data-cy="cf-templates-error">
      Could not load the list of forms. Please try again.
    </p>

    <p v-else-if="!options.length" class="cf-hint" data-cy="cf-no-templates">
      {{ emptyMessage }}
    </p>

    <div v-else>
      <Select
        v-model="selectedId"
        label="Form:"
        data-cy="cf-template-select"
        :options="options"
        :search="true"
        placeholder="Choose a form"
      />
      <!-- The archived copy is not in the default list, so without this an admin
           who archived by mistake attaches a fresh empty form and the answers look
           gone. Deliberately a hint, not a refusal: a second copy is legitimate. -->
      <p
        v-if="selectedIsArchivedHere"
        class="cf-hint cf-hint--warn"
        data-cy="cf-archived-elsewhere-hint"
      >
        This job already has an archived copy of this form. Attaching starts a
        new empty one — to get the earlier answers back, unarchive that copy
        instead (switch the forms list to All).
      </p>

      <p
        v-if="selectedRequiresClose"
        class="cf-hint"
        data-cy="cf-requires-close-hint"
      >
        This form must be completed before the appointment can be closed.
      </p>

      <!-- Attaching from this tab anchors at job level, which gates every visit on
           the job rather than one. Nothing dedupes it, so a second attach of a
           required form is a second job-wide gate, and archiving is the only way to
           clear one. -->
      <p
        v-if="selectedRequiresClose"
        class="cf-hint"
        data-cy="cf-job-level-gate-hint"
      >
        Attached here it applies to the whole job, so every visit has to submit
        it before closing.
      </p>
    </div>
  </Modal>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useStore } from "vuex";

import Modal from "@/components/Modal.vue";
import type { AttachableCustomFormTemplate } from "@/typings/customForm";

// Fetches its own list so the tab does not load templates on every visit for a
// modal that may never open. The parent still owns the attach write.
const props = withDefaults(
  defineProps<{
    saving?: boolean;
    // Templates this job already holds an archived copy of. A hint, not an
    // exclusion: attaching a second copy is legitimate, but it starts empty while
    // the answers stay on the archived row, so the admin needs to know unarchive
    // exists before choosing.
    archivedTemplateIds?: number[];
  }>(),
  { saving: false, archivedTemplateIds: () => [] }
);

const emit = defineEmits<{
  "hide-modal": [];
  submit: [templateId: number | null];
}>();

const store = useStore();

const templates = ref<AttachableCustomFormTemplate[]>([]);
const selectedId = ref<number | null>(null);
const loading = ref(true);
const loadFailed = ref(false);

// Every published template stays on offer, including ones already on the job:
// attaching a second copy is deliberate, so hiding them would block it.
const options = computed(() =>
  templates.value.map((template) => ({
    label: template.name,
    value: template.id
  }))
);

const emptyMessage =
  "No published forms are available to attach. Publish one from Company > Forms & Checklists first.";

const selectedRequiresClose = computed(
  () =>
    templates.value.find((template) => template.id === selectedId.value)
      ?.required_for_close ?? false
);

const selectedIsArchivedHere = computed(
  () =>
    selectedId.value !== null &&
    props.archivedTemplateIds.includes(selectedId.value)
);

onMounted(async () => {
  try {
    templates.value = await store.dispatch(
      "fetchAttachableCustomFormTemplates"
    );
  } catch {
    // Not rethrown — from onMounted that is only an unhandled rejection, and the
    // interceptor already flashed it. Recorded so the modal reports the failure
    // instead of claiming nothing is published.
    templates.value = [];
    loadFailed.value = true;
  } finally {
    loading.value = false;
  }
});
</script>

<style scoped>
.cf-hint {
  margin-top: 4px;
  color: var(--neutral-gray-700);
  font-size: 12px;
}

/* Louder than the other hints because this one is about not losing work. Reuses the
   fields-error token CustomFormCloseoutBlock uses for the same purpose; there is no
   dedicated warning-text token, and --warning-yellow is a background. */
.cf-hint--warn {
  color: var(--sera-color-fields-error);
  font-weight: 500;
}
</style>
